import { useEffect, useRef, useState } from 'react';
import { Storage, adoptServerWrite } from '../lib/storage';
import { useServerApplied } from './serverApplied';

// ─────────────────────────────────────────────────────────────────────
// useSeries — the seriespill domain, lifted out of the Padel24AppMain god
// component (see docs/history/DECOMPOSITION_PLAN.md §5, PR-G; HANDOVER §13.20 / R1).
//
// THIS IS THE MOST DANGEROUS LIFT. It owns TWO §13.20 R1 blanket-persist
// slices. R1 says each such slice is a TRIO that MUST live together:
//   (1) the useState slice,
//   (2) a useRef loaded-flag (starts false),
//   (3) a blanket-persist useEffect that writes Storage on EVERY state change
//       but bails early with `if (!flag.current) return;` so it NEVER writes
//       before the initial load has flipped the flag.
// Splitting state from its flag/effect overwrites saved data with the empty
// init-state — silent loss of tournament/series data on a live platform.
//
//   • activeSeries  ('active_series_v1', TENANT-SCOPED) — the single live serie
//                   (separate track; never parked by new tournaments). useState
//                   init is `null`; the empty case writes '' (the atomic loader
//                   treats a falsy `.value` as absent — do NOT change to skip).
//   • pausedSeries  ('paused_series_v1') — list of paused-serie snapshots.
//
// Both trios were copied VERBATIM from the former monolith — keys, the '' write
// for the null activeSeries case, the `.catch` error-handling style, and the
// Norwegian error strings are unchanged. Do NOT "fix" or normalize them.
//
// WHAT STAYS IN APP (this hook EXPOSES state + setter + ref for each so App can
// keep doing it):
//   • The INITIAL load — it stays in the App shell's ATOMIC mount loader (one
//     large useEffect that reads ~13 domains in one pass; DECOMPOSITION_PLAN
//     principle #1 / R1). That loader reads both keys and flips
//     `activeSeriesLoaded.current = true` / `pausedSeriesLoaded.current = true`.
//     Per the deliberate in/out-of-`cancelled` asymmetry (R1), BOTH series flags
//     flip OUTSIDE the `cancelled` guard — that asymmetry lives in App, not here.
//     Do NOT add a load effect to this hook.
//   • The explicit extra-safety double-write mutators updateActiveSeries /
//     persistSeries / persistPausedSeries / handlePauseSeries etc. — they write
//     these keys explicitly AND call the exposed setters; the blanket-persist
//     effects above are the second, defence-in-depth write.
//   • serieHistoryFocus (a NON-persisted UI slice that sat between the two
//     slices in the former monolith) — it is not part of either trio; it stays
//     in App.
//
// src/ imports ONLY from src/ (Storage from '../lib/storage').
// ─────────────────────────────────────────────────────────────────────

export function useSeries() {
  // Series play runs in a separate track and is never parked by new tournaments
  // (one active series for now; the architecture can later become a list).
  const [activeSeries, setActiveSeries] = useState(null);
  // Paused series: a list of archived snapshots that can be resumed. A series
  // that is paused is hidden from the home screen, but its matches/standings are
  // preserved and can be brought back in. Differs from "retirement", which is
  // final (archived).
  const [pausedSeries, setPausedSeries] = useState([]);
  // Blanket safety for series: persists the active + paused series on every change,
  // independent of call path (same principle as tournaments). updateActiveSeries /
  // persistPausedSeries already do this explicitly; this is an extra guarantee.
  const activeSeriesLoaded = useRef(false);
  const activeFromServer = useServerApplied<any>();
  useEffect(() => {
    if (!activeSeriesLoaded.current) return;
    // The server stored this one itself (#405 — the season transition commits
    // and rewrites the document in one transaction). Writing it back would be
    // redundant, would carry a stale `ifVersion`, and would be the browser
    // writing the session blob again. By identity, so it suppresses exactly
    // one value and the next ordinary change persists as always.
    if (activeFromServer.consume(activeSeries)) return;
    Storage.set('active_series_v1', activeSeries ? JSON.stringify(activeSeries) : '').catch((e) =>
      console.error('[Padel24] Kunne ikke lagre aktiv serie:', e),
    );
  }, [activeSeries, activeFromServer]);
  const pausedSeriesLoaded = useRef(false);
  useEffect(() => {
    if (!pausedSeriesLoaded.current) return;
    Storage.set('paused_series_v1', JSON.stringify(pausedSeries)).catch((e) =>
      console.error('[Padel24] Kunne ikke lagre pausede serier:', e),
    );
  }, [pausedSeries]);

  /**
   * Take a series the SERVER has already applied and stored (#405).
   *
   * Storage-LAST: the write happened server-side, inside the transaction that
   * committed the class's ELO and moved its finished season into history.
   * There is nothing left to persist, only a version to adopt so this
   * browser's next ordinary write is not refused for a change it asked for.
   *
   * Unlike the paused-tournament case, the client genuinely holds what was
   * stored — the response carries the whole series document — so the version
   * can be taken.
   */
  const adoptServerSeries = (next: any, storage?: { key: string; version: number }) => {
    activeFromServer.mark(next);
    if (storage) adoptServerWrite(storage.key, JSON.stringify(next), storage.version);
    setActiveSeries(next);
  };

  return {
    adoptServerSeries,
    activeSeries,
    pausedSeries,
    // Setters + the loaded-flag refs are exposed for the App shell's atomic mount
    // loader (which flips both refs after reading the values, OUTSIDE the
    // `cancelled` guard) and for the App-side double-write mutators
    // (updateActiveSeries / persistPausedSeries / handlePauseSeries / …) that
    // call the setters. The blanket-persist effects above stay owned here.
    setActiveSeries,
    activeSeriesLoaded,
    setPausedSeries,
    pausedSeriesLoaded,
  };
}
